import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle2, Pencil, XCircle, Wifi } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button, Card, CardContent, CardHeader, CardTitle, Badge, Spinner,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ApiErrorDisplay } from '@/components/features/error';
import { IqServiceForm } from '@/components/features/iq-services';
import {
  useIqService, useUpdateIqService, useDeleteIqService,
  useTestIqService, useIqServiceServices,
} from '@/hooks';
import { ApiError } from '@/lib/api/client';
import { formatDate } from '@/lib/utils';
import type {
  IqServiceStatus, CreateIqServiceRequest, UpdateIqServiceRequest,
  TestErrorCategory,
} from '@/types/iq-service';

const statusVariant: Record<IqServiceStatus, 'success' | 'warning' | 'destructive'> = {
  active: 'success',
  inactive: 'warning',
  suspended: 'destructive',
};

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

/**
 * IQ-Service detail page with read view, edit, delete, connectivity test, and assigned services.
 * Route: /iq-services/:id (platform_admin only)
 */
export function IQServiceDetailPage() {
  const { t } = useTranslation('iq-services');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);

  const { data: service, isLoading, error, refetch } = useIqService(id);
  const updateMutation = useUpdateIqService();
  const deleteMutation = useDeleteIqService();
  const testMutation = useTestIqService();
  const { data: assignedServices } = useIqServiceServices(id);

  if (!id) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Missing IQ-Service ID</p>
        <Button variant="link" onClick={() => navigate('/iq-services')}>
          {t('detail.back')}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ApiErrorDisplay error={error} onRetry={() => refetch()} />;
  }

  if (!service) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">{t('list.empty')}</p>
        <Button variant="link" onClick={() => navigate('/iq-services')}>
          {t('detail.back')}
        </Button>
      </div>
    );
  }

  const handleUpdate = async (data: CreateIqServiceRequest | UpdateIqServiceRequest) => {
    try {
      await updateMutation.mutateAsync({ id, data: data as UpdateIqServiceRequest });
      toast.success(t('actions.updateSuccess'));
      setIsEditing(false);
    } catch {
      // 409 is handled in the hook (toast + refetch)
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(id);
      toast.success(t('actions.deleteSuccess'));
      navigate('/iq-services');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(t('actions.hasServicesError'));
      } else if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error(String(err));
      }
    }
  };

  const handleTest = async () => {
    try {
      const result = await testMutation.mutateAsync(id);
      if (result.success) {
        toast.success(t('test.success', { ms: result.responseTimeMs ?? '?' }));
      } else {
        const category = result.errorCategory as TestErrorCategory;
        toast.error(t(`test.error.${category}`));
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        toast.error(t('test.rateLimitExceeded'));
      } else if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error(String(err));
      }
    }
  };

  const canDelete = (service.assignedServiceCount ?? 0) === 0;
  const services = assignedServices?.data ?? [];

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{service.displayName}</h1>
          <p className="mt-1 font-mono text-sm text-muted-foreground">{service.iqServiceId}</p>
        </div>
        <div className="flex gap-2">
          {!isEditing && (
            <>
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('actions.edit')}
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={!canDelete}
                    loading={deleteMutation.isPending}
                  >
                    {t('actions.delete')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t('actions.confirmDelete.title', { name: service.displayName })}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('actions.confirmDelete.description')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('actions.confirmDelete.cancel')}</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete}>
                      {t('actions.confirmDelete.confirm')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}

          <Button variant="outline" size="sm" onClick={() => navigate('/iq-services')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('detail.back')}
          </Button>
        </div>
      </div>

      {/* Edit mode */}
      {isEditing ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('form.editTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <IqServiceForm
              mode="edit"
              initialData={service}
              onSubmit={handleUpdate}
              onCancel={() => setIsEditing(false)}
              isLoading={updateMutation.isPending}
              error={updateMutation.error}
            />
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Read-only view */}
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('detail.info')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <InfoField label={t('detail.fields.displayName')}>
                  <p className="font-medium">{service.displayName}</p>
                </InfoField>
                <InfoField label={t('detail.fields.status')}>
                  <Badge variant={statusVariant[service.status]}>
                    {t(`status.${service.status}`)}
                  </Badge>
                </InfoField>
                <InfoField label={t('detail.fields.gatewayMode')}>
                  <Badge variant="outline">{t(`gatewayMode.${service.gatewayMode}`)}</Badge>
                </InfoField>
                <InfoField label={t('detail.fields.merchantName')}>
                  <p>{service.merchantName}</p>
                </InfoField>
                <InfoField label={t('detail.fields.merchantId')}>
                  <p className="font-mono text-sm">{service.merchantId}</p>
                </InfoField>
                {service.description && (
                  <InfoField label={t('detail.fields.description')}>
                    <p>{service.description}</p>
                  </InfoField>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('detail.gateway')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <InfoField label={t('detail.fields.gatewayBaseUrl')}>
                  <p className="font-mono text-sm break-all">{service.gatewayBaseUrl}</p>
                </InfoField>
                <InfoField label={t('detail.fields.gatewayEndpoint')}>
                  <p className="font-mono text-sm">{service.gatewayEndpoint ?? '—'}</p>
                </InfoField>
                <InfoField label={t('detail.fields.username')}>
                  <p className="font-mono text-sm">{service.credentials.username}</p>
                </InfoField>
                <InfoField label={t('detail.fields.password')}>
                  <p className="font-mono text-sm">{service.credentials.password}</p>
                </InfoField>
                <InfoField label={t('detail.fields.assignedServiceCount')}>
                  <p>{service.assignedServiceCount ?? 0}</p>
                </InfoField>
                <InfoField label={t('detail.fields.createdAt')}>
                  <p>{formatDate(service.createdAt)}</p>
                </InfoField>
                <InfoField label={t('detail.fields.updatedAt')}>
                  <p>{formatDate(service.updatedAt)}</p>
                </InfoField>
                {service.updatedBy && (
                  <InfoField label={t('detail.fields.updatedBy')}>
                    <p>{service.updatedBy}</p>
                  </InfoField>
                )}
              </CardContent>
            </Card>
          </div>

          {/* M-9: Connectivity Test */}
          <Card className="mt-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">{t('test.button')}</CardTitle>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">{t('test.rateLimitHint')}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTest}
                  disabled={testMutation.isPending}
                >
                  <Wifi className="mr-2 h-4 w-4" />
                  {testMutation.isPending ? t('test.testing') : t('test.button')}
                </Button>
              </div>
            </CardHeader>
            {testMutation.data && (
              <CardContent>
                {testMutation.data.success ? (
                  <div className="flex items-center gap-2 text-success">
                    <CheckCircle2 className="h-5 w-5" />
                    <span>{t('test.success', { ms: testMutation.data.responseTimeMs ?? '?' })}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-destructive">
                    <XCircle className="h-5 w-5" />
                    <span>{t(`test.error.${testMutation.data.errorCategory}`)}</span>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* M-11: Assigned Services */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-base">{t('assignedServices.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              {services.length === 0 ? (
                <p className="py-4 text-center text-muted-foreground">{t('assignedServices.empty')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('assignedServices.columns.serviceId')}</TableHead>
                      <TableHead>{t('assignedServices.columns.merchantId')}</TableHead>
                      <TableHead>{t('assignedServices.columns.description')}</TableHead>
                      <TableHead className="text-right">{t('assignedServices.columns.grossAmount')}</TableHead>
                      <TableHead>{t('assignedServices.columns.currency')}</TableHead>
                      <TableHead>{t('assignedServices.columns.status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {services.map((svc) => (
                      <TableRow
                        key={svc.serviceId}
                        className="cursor-pointer hover:bg-accent/50"
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/services/${svc.serviceId}`)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/services/${svc.serviceId}`); } }}
                      >
                        <TableCell className="font-mono text-xs">{svc.serviceId}</TableCell>
                        <TableCell className="font-mono text-xs">{svc.merchantId}</TableCell>
                        <TableCell>{svc.description}</TableCell>
                        <TableCell className="text-right">{(svc.grossAmount / 100).toFixed(2)}</TableCell>
                        <TableCell>{svc.currency}</TableCell>
                        <TableCell>
                          <Badge variant={svc.status === 'active' ? 'success' : 'warning'}>
                            {svc.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
